import type { ICommandBarItemProps } from '@fluentui/react';
import type { UserInfo } from '@auth/types';
import { hasAnyPermission } from '@auth/permissions';
import {
  APP_CATALOG,
  DEPARTMENT_ICONS,
  DEPARTMENT_LABELS,
  DEPARTMENT_ORDER,
  TOOLS_SUBGROUP_LABELS,
  TOOLS_SUBGROUP_ORDER,
  type AppCatalogEntry,
  type AppDepartment,
  type ToolsSubgroup,
} from './appCatalog';

export function hasAccess(entry: AppCatalogEntry, user: UserInfo | null | undefined): boolean {
  if (!user) return false;
  if (!entry.permissions?.length) return false;
  return hasAnyPermission(user, entry.permissions);
}

export function getAccessibleApps(user: UserInfo | null | undefined): AppCatalogEntry[] {
  return APP_CATALOG.filter(entry => hasAccess(entry, user));
}

export function getQuickAccessApps(user: UserInfo | null | undefined): AppCatalogEntry[] {
  return getAccessibleApps(user)
    .filter(entry => entry.quickAccess)
    .sort(compareEntries);
}

export interface DepartmentGroup {
  department: AppDepartment;
  label: string;
  apps: AppCatalogEntry[];
}

export function groupByDepartment(apps: AppCatalogEntry[]): DepartmentGroup[] {
  return DEPARTMENT_ORDER
    .map(department => ({
      department,
      label: DEPARTMENT_LABELS[department],
      apps: apps
        .filter(app => app.department === department && !app.quickAccess)
        .sort(compareEntries),
    }))
    .filter(group => group.apps.length > 0);
}

export interface ToolsSubgroupGroup {
  subgroup: ToolsSubgroup;
  label: string;
  apps: AppCatalogEntry[];
}

export function groupToolsBySubgroup(apps: AppCatalogEntry[]): ToolsSubgroupGroup[] {
  return TOOLS_SUBGROUP_ORDER
    .map(subgroup => ({
      subgroup,
      label: TOOLS_SUBGROUP_LABELS[subgroup],
      apps: apps
        .filter(app => app.department === 'tools' && app.toolsSubgroup === subgroup)
        .sort(compareEntries),
    }))
    .filter(group => group.apps.length > 0);
}

function compareEntries(a: AppCatalogEntry, b: AppCatalogEntry): number {
  const orderDiff = (a.sortOrder ?? 999) - (b.sortOrder ?? 999);
  if (orderDiff !== 0) return orderDiff;
  return a.title.localeCompare(b.title);
}

function buildCatalogNavItem(
  entry: AppCatalogEntry,
  navigate: (path: string) => void
): ICommandBarItemProps {
  if (entry.navChildren?.length) {
    return {
      key: entry.id,
      text: entry.navTitle ?? entry.title,
      iconProps: { iconName: entry.icon },
      subMenuProps: {
        items: entry.navChildren.map(child => ({
          key: child.id,
          text: child.title,
          iconProps: { iconName: child.icon },
          onClick: () => navigate(child.route),
        })),
      },
    };
  }

  return {
    key: entry.id,
    text: entry.title,
    iconProps: { iconName: entry.icon },
    onClick: () => navigate(entry.route),
  };
}

function buildDepartmentNavItems(
  apps: AppCatalogEntry[],
  department: AppDepartment,
  navigate: (path: string) => void
): ICommandBarItemProps[] {
  const departmentApps = apps.filter(app => app.department === department);

  if (department === 'tools') {
    const subgroupItems: ICommandBarItemProps[] = [];

    for (const subgroup of TOOLS_SUBGROUP_ORDER) {
      const subgroupApps = departmentApps.filter(app => app.toolsSubgroup === subgroup);
      if (subgroupApps.length === 0) continue;

      subgroupItems.push({
        key: `tools-${subgroup}`,
        text: TOOLS_SUBGROUP_LABELS[subgroup],
        subMenuProps: {
          items: subgroupApps.map(app => buildCatalogNavItem(app, navigate)),
        },
      });
    }

    return subgroupItems;
  }

  return departmentApps.map(app => buildCatalogNavItem(app, navigate));
}

export function buildDepartmentMenus(
  user: UserInfo | null | undefined,
  navigate: (path: string) => void
): ICommandBarItemProps[] {
  const accessibleApps = getAccessibleApps(user);

  const menus: ICommandBarItemProps[] = [];

  for (const department of DEPARTMENT_ORDER) {
    const items = buildDepartmentNavItems(accessibleApps, department, navigate);
    if (items.length === 0) continue;

    menus.push({
      key: department,
      text: DEPARTMENT_LABELS[department],
      iconProps: { iconName: DEPARTMENT_ICONS[department] },
      subMenuProps: { items },
    });
  }

  return menus;
}
